import anylogger from '@app/anylogger'
import { CaretPos } from '@app/hooks'
import { IMdxSource } from '@app/mdxsource'
import { fromMarkdown } from 'mdast-util-from-markdown'
import { mdxFromMarkdown } from 'mdast-util-mdx'
import { mdxjs } from 'micromark-extension-mdxjs'

const log = anylogger('mdxParsing')

export interface PartPosition {
	column: number
	line: number
	offset: number
}
export type Position = { start: PartPosition; end: PartPosition }
interface JsxAttribute {
	name: string
	value: string | any // value can be null for boolean attributes
}

// Base interface with common properties
export interface BaseMDXPart {
	position: Position
	content: string
}

// Text-specific interface
export interface TextMDXPart extends BaseMDXPart {
	type: 'text'
	boldPos?: Position
	italicPos?: Position
}

// JSX-specific interface
export interface JsxMDXPart extends BaseMDXPart {
	type: 'jsx'
	name: string
	attributes: JsxAttribute[]
}

// Link-specific interface
export interface LinkMDXPart extends BaseMDXPart {
	type: 'link'
	url: string
	text: string
	textPosition: Position
}
export interface StrongMDXPart extends BaseMDXPart {
	type: 'strong'
}
export interface EmphasisMDXPart extends BaseMDXPart {
	type: 'emphasis'
}
export type MDXPart = TextMDXPart | JsxMDXPart | LinkMDXPart | StrongMDXPart | EmphasisMDXPart

export function isLink(part: MDXPart | undefined): part is LinkMDXPart {
	return part ? part.type === 'link' : false
}
export function isJsx(part: MDXPart | undefined): part is JsxMDXPart {
	return part ? part.type === 'jsx' : false
}
export function isText(part: MDXPart | undefined): part is TextMDXPart {
	return part ? part.type === 'text' : false
}

export function parseMDX(content: string): MDXPart[] {
	try {
		const tree = fromMarkdown(content, {
			extensions: [mdxjs()],
			mdastExtensions: [mdxFromMarkdown()]
		})

		let boldPos: Position | undefined = undefined
		let italicPos: Position | undefined = undefined
		const parts: MDXPart[] = []

		// Helper function to traverse the AST
		const traverseNode = (origNode: any) => {
			const node = { ...origNode }
			if (node.type === 'mdxJsxFlowElement' || node.type === 'mdxJsxTextElement') {
				const part: JsxMDXPart = {
					type: 'jsx',
					content: content.slice(node.position.start.offset, node.position.end.offset),
					name: node.name,
					attributes: node.attributes || [],
					position: clonePosition(node.position)
				}
				parts.push(part)
			} else if (node.type === 'text') {
				const part: TextMDXPart = {
					type: 'text',
					content: node.value,
					position: clonePosition(node.position),
					boldPos,
					italicPos
				}
				parts.push(part)
			} else if (node.type === 'strong') {
				boldPos = clonePosition(node.position)
				try {
					if (node.children) node.children.forEach(traverseNode)
				} finally {
					boldPos = undefined
				}
			} else if (node.type === 'emphasis') {
				italicPos = clonePosition(node.position)
				try {
					if (node.children) node.children.forEach(traverseNode)
				} finally {
					italicPos = undefined
				}
			} else if (node.type === 'link') {
				const part: LinkMDXPart = {
					type: 'link',
					content: content.slice(node.position.start.offset, node.position.end.offset),
					text: node.children?.[0]?.value || '',
					textPosition: clonePosition(node.children?.[0]?.position),
					url: node.url,
					position: clonePosition(node.position)
				}

				parts.push(part)
			} else {
				// log('Unkown node type', node.type, node)

				if (node.children) {
					node.children.forEach(traverseNode)
				}
			}
		}

		tree.children.forEach(traverseNode)

		// log('positions', parts)

		return parts
	} catch (error) {
		log('Error parsing MDX:', error)
		return [{ type: 'text', content } as TextMDXPart]
	}
}

export function findPart(caretPos: CaretPos, mdxParts: MDXPart[]) {
	let atStartOfPart = false
	let atEndOfPart = false
	let foundPart = undefined
	let otherParts = []
	for (let ii = 0; ii < mdxParts.length; ++ii) {
		const part = mdxParts[ii]
		if (part.position) {
			let position = getVirtualPosition(part)
			const partStart = position.start.offset
			const partEnd = position.end.offset
			if (caretPos.start == caretPos.end) {
				if (isLink(part) || isJsx(part)) {
					if (partStart == caretPos.start) atStartOfPart = true
					if (partEnd == caretPos.start) atEndOfPart = true
				}
				if (caretPos.start > partStart && caretPos.start < partEnd) {
					return { part, otherParts, atStartOfPart, atEndOfPart }
				}
			} else {
				if (foundPart) {
					if (caretPos.end > partStart) {
						otherParts.push(part)
					}
					if (caretPos.end < partEnd) return { part: foundPart, otherParts, atStartOfPart, atEndOfPart }
				} else if (caretPos.start >= partStart && caretPos.end <= partEnd) {
					// log('foundPart', part)

					return { part, otherParts, atStartOfPart, atEndOfPart }
				} else if (
					(caretPos.start < partEnd && caretPos.end > partStart) || // for parts where the start caret is in th middle of the part
					(caretPos.end < partStart && caretPos.start < partEnd) // for parts where the end caret is in the middle of the part
				) {
					foundPart = part
				}
			}

			// if (isLink(part) || isJsx(part)) {
			// 	if (partStart == caretPos.start) atStartOfPart = true
			// 	if (partEnd == caretPos.start) atEndOfPart = true
			// }
			// if (foundPart) return { part: foundPart, atStartOfPart, atEndOfPart }
		}
	}
	// log('No part found for caret position:', caretPos, mdxParts)
	return { part: undefined, otherParts, atEndOfPart, atStartOfPart }
}
export function getVirtualPosition(part: MDXPart): Position {
	let position: Position = { start: { ...part.position.start }, end: { ...part.position.end } }
	if (isText(part)) {
		if (part.boldPos) {
			if (part.boldPos.start.offset < position.start.offset) position.start = part.boldPos.start
			if (part.boldPos.end.offset > position.end.offset) position.end = part.boldPos.end
		}
		if (part.italicPos) {
			if (part.italicPos.start.offset < position.start.offset) position.start = part.italicPos.start
			if (part.italicPos.end.offset > position.end.offset) position.end = part.italicPos.end
		}
	}
	return position
}
function clonePosition(position: Position): Position {
	return {
		start: { ...position.start },
		end: { ...position.end }
	}
}
export function createJsxContentfromPart(data: JsxMDXPart) {
	let props = ''
	if (data.attributes) {
		data.attributes.forEach((attr) => {
			if (attr.value == null) return
			let val = ''
			if (typeof attr.value === 'object') {
				val = `{${JSON.stringify(attr.value.value)}}`
			} else val = `"${attr.value}"`

			if (!val) return
			props += `${attr.name}=${val} `
		})
	}
	return `<${data.name} ${props}/>`
}
export function createJsxContent(data: JsxMDXPart, props: string) {
	return `<${data.name} ${props}/>`
}
export async function replacePageDataLinksWithReferenceData(content: string, mdxSource: IMdxSource): Promise<string> {
	const parts = parseMDX(content)
	for (let ii = parts.length - 1; ii >= 0; ii--) {
		const part = parts[ii]
		if (part.type != 'jsx') continue
		if (!['PageCards', 'PageLinks'].includes(part.name)) continue
		const jsxPart = part as JsxMDXPart

		let madeChanges = false
		for (let jj = 0; jj < jsxPart.attributes.length; jj++) {
			const att = jsxPart.attributes[jj]
			if (att.name == 'pageData' && att.value.value != 'pageData' && att.value.value != 'childData') {
				const pages = JSON.parse(att.value.value)
				log('Processing Page Data', pages)
				madeChanges = true
				const pageData = await mdxSource.GetPageData(pages)
				att.value.value = pageData
			}
		}
		if (!madeChanges) continue
		const start = jsxPart.position.start.offset
		const end = jsxPart.position.end.offset

		const newContent = createJsxContentfromPart(jsxPart)

		content = content.substring(0, start) + newContent + content.substring(end)
	}
	return content
}
